import { useCallback, useEffect, useRef, useState } from 'react';
import { config } from '../config/env';
import type {
  ApprovalContext,
  ApprovalEscalation,
  ApprovalMessage,
  ApprovalPermission,
  ApprovalPhase,
  ApprovalSample,
} from '../types/approvals';
import { useApiFetch } from './useApiFetch';

interface ReviewState {
  phase: ApprovalPhase;
  samples: ApprovalSample[];
  selectedId: string | null;
  context: ApprovalContext[];
  contextTruncated: boolean;
  escalations: ApprovalEscalation[];
  permission: ApprovalPermission | null;
  queuedCount: number;
  message: string | null;
}

const INITIAL: ReviewState = {
  phase: 'connecting',
  samples: [],
  selectedId: null,
  context: [],
  contextTruncated: false,
  escalations: [],
  permission: null,
  queuedCount: 0,
  message: null,
};

const MAX_CONTEXT_ENTRIES = 100;
const MAX_CONTEXT_TEXT = 16000;

/** One mounted review owns one socket. Closing never answers a permission. */
export function useApprovals(jobId: string) {
  const { apiFetch } = useApiFetch();
  const [state, setState] = useState<ReviewState>(INITIAL);
  const socket = useRef<WebSocket | null>(null);
  // Clear synchronously on a click/disconnect so a double tap cannot send twice.
  const pending = useRef<ApprovalPermission | null>(null);
  const unconfirmed = useRef<string | null>(null);
  const displayedId = useRef<string | null>(null);
  const queued = useRef(new Map<string, ApprovalPermission>());
  const selected = useRef<string | null>(null);
  const refreshPending = useRef(true);
  const nextContextId = useRef(0);

  useEffect(() => {
    let active = true;
    let ws: WebSocket | null = null;
    // The server sends the initial listing before reading browser commands.
    refreshPending.current = true;
    const waitingRequests = queued.current;
    const fail = (message?: string, close = true) => {
      if (!active) return;
      pending.current = null;
      queued.current.clear();
      refreshPending.current = false;
      const decisionMayHaveApplied = unconfirmed.current !== null;
      setState(previous =>
        previous.phase === 'applied' ||
        (previous.phase === 'unavailable' && message === undefined)
          ? { ...previous, queuedCount: 0 }
          : {
              ...previous,
              phase: 'unavailable',
              queuedCount: 0,
              message: decisionMayHaveApplied
                ? 'Disconnected before confirmation. Check the transcript before reconnecting; your decision may have applied.'
                : (message ??
                  'Disconnected. Reconnect to get the current request.'),
            }
      );
      if (close && message !== undefined) ws?.close();
    };

    void (async () => {
      const path = `/approvals/${encodeURIComponent(jobId)}`;
      const response = await apiFetch(path, undefined, {
        returnNonOkResponse: true,
      });
      if (!active) return;
      if (!response?.ok) {
        let message = 'Could not connect to approvals. Try reconnecting.';
        if (response) {
          const body = await response.text();
          try {
            const error = JSON.parse(body) as {
              detail?: unknown;
              message?: unknown;
              title?: unknown;
            };
            const detail = error.detail ?? error.message ?? error.title;
            if (typeof detail === 'string') message = detail;
          } catch {
            if (body) message = body;
          }
        }
        fail(message);
        return;
      }
      const url = new URL(`${config.apiBaseUrl}${path}`, window.location.href);
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      ws = new WebSocket(url);
      socket.current = ws;
      ws.onmessage = event => {
        if (!active) return;
        let frame: ApprovalMessage;
        try {
          frame = JSON.parse(String(event.data)) as ApprovalMessage;
        } catch {
          fail(
            'The approval connection returned an invalid response. Reconnect to try again.'
          );
          return;
        }
        switch (frame.type) {
          case 'sessions':
            refreshPending.current = false;
            setState(previous => ({
              ...previous,
              samples: frame.samples,
              phase: selected.current ? previous.phase : 'ready',
            }));
            break;
          case 'update': {
            if (frame.params.sessionId !== selected.current) break;
            if (frame.params._meta?.['inspect.picker.targets']) break;
            const update = frame.params.update;
            const kind = String(update.sessionUpdate ?? 'update');
            if (
              ![
                'agent_message_chunk',
                'user_message_chunk',
                'agent_thought_chunk',
                'tool_call',
                'tool_call_update',
              ].includes(kind)
            )
              break;
            const content = update.content as
              { type?: string; text?: string } | undefined;
            const isText =
              content?.type === 'text' && typeof content.text === 'string';
            const text = isText
              ? content.text!
              : JSON.stringify(update, null, 2);
            const meta = update._meta as Record<string, unknown> | undefined;
            const metadata: Record<string, string> = {};
            for (const key of [
              'inspect.message_role',
              'inspect.user_source',
              'inspect.message_id',
              'inspect.model',
              'inspect.model_fallback',
              'inspect.model_event_uuid',
            ]) {
              if (typeof meta?.[key] === 'string') metadata[key] = meta[key];
            }
            const contextId = nextContextId.current++;
            setState(previous => {
              const last = previous.context.at(-1);
              const entry = {
                id: contextId,
                kind,
                text,
                markdown: isText,
                metadata,
                messageId:
                  typeof update.messageId === 'string'
                    ? update.messageId
                    : undefined,
                title:
                  typeof update.title === 'string' ? update.title : undefined,
              };
              const canMerge = (item: ApprovalContext) =>
                isText &&
                item.kind === kind &&
                item.markdown &&
                entry.messageId &&
                item.messageId === entry.messageId &&
                Object.entries(metadata).every(
                  ([key, value]) =>
                    item.metadata[key] === undefined ||
                    item.metadata[key] === value
                );
              if (isText && !text) {
                return {
                  ...previous,
                  context: entry.messageId
                    ? previous.context.map(item =>
                        canMerge(item)
                          ? {
                              ...item,
                              metadata: { ...item.metadata, ...metadata },
                            }
                          : item
                      )
                    : previous.context,
                };
              }
              const context =
                last && canMerge(last)
                  ? [
                      ...previous.context.slice(0, -1),
                      {
                        ...last,
                        metadata: { ...last.metadata, ...metadata },
                        text: last.text + text,
                      },
                    ]
                  : [...previous.context, entry];
              const latest = context[context.length - 1];
              const contextTruncated =
                previous.contextTruncated ||
                context.length > MAX_CONTEXT_ENTRIES ||
                latest.text.length > MAX_CONTEXT_TEXT;
              latest.text = latest.text.slice(-MAX_CONTEXT_TEXT);
              return {
                ...previous,
                context: context.slice(-MAX_CONTEXT_ENTRIES),
                contextTruncated,
              };
            });
            break;
          }
          case 'escalation': {
            const escalation: ApprovalEscalation = {
              tool_call_id: frame.tool_call_id,
              approver: frame.approver,
              explanation:
                frame.explanation?.slice(0, MAX_CONTEXT_TEXT) ?? null,
              message: frame.message?.slice(0, MAX_CONTEXT_TEXT) ?? null,
              truncated:
                (frame.explanation?.length ?? 0) > MAX_CONTEXT_TEXT ||
                (frame.message?.length ?? 0) > MAX_CONTEXT_TEXT,
            };
            setState(previous => ({
              ...previous,
              escalations: [
                ...previous.escalations.filter(
                  item =>
                    item.tool_call_id !== frame.tool_call_id ||
                    item.approver !== frame.approver
                ),
                escalation,
              ].slice(-MAX_CONTEXT_ENTRIES),
            }));
            break;
          }
          case 'permission': {
            if (frame.params.sessionId !== selected.current) break;
            const permission = {
              ...frame,
              contextEndId: nextContextId.current,
            };
            if (displayedId.current !== null) {
              if (frame.request_id !== displayedId.current) {
                queued.current.set(frame.request_id, permission);
                setState(previous => ({
                  ...previous,
                  queuedCount: queued.current.size,
                }));
              }
              break;
            }
            displayedId.current = frame.request_id;
            pending.current = permission;
            setState(previous => ({
              ...previous,
              permission,
              phase: 'pending',
              message: null,
            }));
            break;
          }
          case 'decision':
          case 'invalidated':
            if (frame.type === 'invalidated' && frame.connection_closed) {
              if (
                frame.decision_sent === false &&
                unconfirmed.current === frame.request_id
              )
                unconfirmed.current = null;
              fail(frame.reason, false);
              break;
            }
            if (
              unconfirmed.current === frame.request_id &&
              (frame.type === 'invalidated' || frame.status === 'applied')
            )
              unconfirmed.current = null;
            queued.current.delete(frame.request_id);
            if (pending.current?.request_id === frame.request_id)
              pending.current = null;
            setState(previous =>
              previous.permission?.request_id === frame.request_id
                ? {
                    ...previous,
                    phase:
                      frame.type === 'decision' ? frame.status : 'unavailable',
                    message:
                      frame.type === 'decision'
                        ? (frame.decision ?? null)
                        : frame.reason,
                    queuedCount: queued.current.size,
                  }
                : { ...previous, queuedCount: queued.current.size }
            );
            break;
          case 'error':
          case 'closed':
            fail(frame.type === 'error' ? frame.message : frame.reason);
            break;
          case 'ping':
            break;
        }
      };
      ws.onerror = () =>
        fail('The approval connection failed. Reconnect to try again.');
      ws.onclose = () => fail();
    })().catch(() => fail('Could not load approvals. Reconnect to try again.'));

    return () => {
      active = false;
      pending.current = null;
      unconfirmed.current = null;
      displayedId.current = null;
      waitingRequests.clear();
      selected.current = null;
      refreshPending.current = false;
      socket.current = null;
      ws?.close();
    };
  }, [apiFetch, jobId]);

  const select = useCallback((sessionId: string) => {
    if (socket.current?.readyState !== WebSocket.OPEN || selected.current)
      return;
    selected.current = sessionId;
    setState(previous => ({
      ...previous,
      selectedId: sessionId,
      phase: 'reviewing',
    }));
    socket.current.send(
      JSON.stringify({ type: 'select', session_id: sessionId })
    );
  }, []);
  const decide = (requestId: string, optionId: string) => {
    const permission = pending.current;
    if (
      socket.current?.readyState !== WebSocket.OPEN ||
      !permission ||
      permission.request_id !== requestId ||
      !permission.params.options.some(option => option.optionId === optionId)
    )
      return;
    pending.current = null;
    unconfirmed.current = permission.request_id;
    setState(previous => ({ ...previous, phase: 'sending' }));
    socket.current.send(
      JSON.stringify({
        type: 'decision',
        request_id: permission.request_id,
        option_id: optionId,
      })
    );
  };
  const refresh = useCallback(() => {
    if (
      socket.current?.readyState === WebSocket.OPEN &&
      !selected.current &&
      !refreshPending.current
    ) {
      refreshPending.current = true;
      socket.current.send(JSON.stringify({ type: 'refresh' }));
    }
  }, []);
  const nextRequest = () => {
    if (
      socket.current?.readyState !== WebSocket.OPEN ||
      pending.current ||
      displayedId.current !== state.permission?.request_id ||
      (state.phase !== 'applied' && state.phase !== 'unavailable')
    )
      return;
    const next = queued.current.values().next().value;
    if (!next) return;
    queued.current.delete(next.request_id);
    displayedId.current = next.request_id;
    pending.current = next;
    setState(previous => ({
      ...previous,
      permission: next,
      phase: 'pending',
      message: null,
      queuedCount: queued.current.size,
    }));
  };
  return { ...state, select, decide, refresh, nextRequest };
}
